import { useToastManager } from "@porsche-design-system/components-react"
import type {
	InventoryAndIntakeView,
	InventoryInformationEditView,
	InventoryInformationView,
} from "@slfinrtl/ui-bff-routes"
import { getInventoryInformationEditViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { AnimatedStage } from "src/components"
import { useEditStageContextForStage } from "src/context/editStageContext/useEditStageContext"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff } from "src/hooks"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { useScrollToStage } from "src/screens/vehicleDetails/common/useScrollToStage/useScrollToStage"
import { useEditActionButton } from "src/screens/vehicleDetails/hooks/useEditActionButton"
import { useVehicleDetailsContext } from "../../../context/VehicleDetailsContext"
import { InventoryAndIntakeFormContainer } from "./InventoryAndIntakeFormContainer"
import { InventoryAndIntake } from "./components/InventoryAndIntake"
import { inventoryAndIntakeContainerMessages } from "./inventoryAndIntakeContainer.messages"

interface InventoryAndIntakeContainerProps {
	setView: (view: InventoryInformationView) => void
	view: InventoryAndIntakeView
}

export const InventoryAndIntakeContainer = withErrorHandler(({ setView, view }: InventoryAndIntakeContainerProps) => {
	const { inventoryId, notify } = useVehicleDetailsContext()
	const { mode, setEditStage } = useEditStageContextForStage<VehicleDetailsStage>("inventoryIntake")
	const [editView, setEditView] = useState<InventoryInformationEditView | undefined>()
	const [isEditViewLoading, setIsEditViewLoading] = useState(false)

	const ref = useRef<HTMLDivElement | null>(null)

	const { formatMessage } = useIntl()
	const { addMessage } = useToastManager()

	const fetchEditView = useBff(
		getInventoryInformationEditViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const handleEditClick = useCallback((): Promise<void> => {
		setIsEditViewLoading(true)
		return fetchEditView()
			.then((result) => {
				setEditView(result)
				setEditStage("inventoryIntake")
			})
			.finally(() => setIsEditViewLoading(false))
	}, [fetchEditView, setEditStage])

	const onExit = useCallback(() => {
		setEditStage(undefined)
	}, [setEditStage])

	const onSaved = useCallback(
		(inventoryInformationView: InventoryInformationView) => {
			setView(inventoryInformationView)
			onExit()
			addMessage({
				state: "success",
				text: formatMessage(commonMessages.saveSuccess),
			})
			notify({
				event: "inventoryInformationUpdated",
				payload: inventoryInformationView,
			})
			notify({
				event: "inventoryInformationViewChanged",
				payload: inventoryInformationView,
			})
		},
		[onExit, setView, addMessage, formatMessage, notify],
	)

	useScrollToStage({
		callbackFn: handleEditClick,
		editStage: "inventoryIntake",
		ref,
	})

	const actionButton = useEditActionButton("inventoryIntake", view.inventoryEdit, {
		fetch: handleEditClick,
		isLoading: isEditViewLoading,
	})

	return (
		<AnimatedStage
			ref={ref}
			title={formatMessage(inventoryAndIntakeContainerMessages.title)}
			actionButtons={actionButton}
			data-testid="inventory-and-intake_container"
		>
			<ErrorNotificationInline />
			{mode !== "edit" || !editView ? (
				<InventoryAndIntake view={view} />
			) : (
				<InventoryAndIntakeFormContainer
					inventoryId={inventoryId}
					view={editView}
					onCancel={onExit}
					onSaved={onSaved}
				/>
			)}
		</AnimatedStage>
	)
})
